import {
  isSupabaseConfigured,
  missingSupabaseConfig,
  supabaseUrl,
} from "./config";
import { supabase } from "./client";
import { withRequestDeadline } from "./requestDeadline";
import { validateQrCustomerSession } from "./qrCustomerSessionService";
import { normalizeQrMenuStyle } from "../local/qrMenuStyle";
import { normalizeQrCategoryOrder, normalizeQrSubcategoryOrder } from "../local/qrCategoryOrder";
import { normalizeWaiterAssistanceConfig } from "../local/waiterAssistance";
import { filterKitchenOrderItems, orderRequiresKitchen } from "../local/orderModel";
import {
  DEFAULT_BRANCH_COMMISSION_RATE,
  DEFAULT_BRANCH_PAYMENT_PROVIDER,
  DEFAULT_BRANCH_PAYMENT_PROVIDER_STATUS,
  DEFAULT_BRANCH_PAYMENT_SPLIT_MODE,
  DEFAULT_KIOSK_PAYMENT_METHODS,
  DEFAULT_QR_PAYMENT_METHODS,
  normalizeCommissionRate,
  normalizePaymentMethods,
  normalizePaymentProvider,
  normalizePaymentProviderStatus,
  normalizePaymentSplitMode,
  resolveBranchChannelCommissionRates,
} from "../local/paymentSettings";
import { normalizeMenuQrWalletProviders } from "../payments/menuQrWalletProviders";

const ensureSupabaseReady = () => {
  if (!isSupabaseConfigured || !supabase) {
    throw new Error(
      `Faltan variables de entorno para Supabase: ${missingSupabaseConfig.join(", ")}`
    );
  }
};

const normalizeError = (error, fallbackMessage) => {
  if (!error) {
    return new Error(fallbackMessage);
  }

  return new Error(error.message || fallbackMessage);
};

const DEFAULT_QR_ACCESS_DURATION_MINUTES = 120;
const DISPLAY_PAIRING_PREFIX = "MZ";
const PUBLIC_MENU_CACHE_TTL_MS = 20 * 1000;
const PUBLIC_MENU_PERSISTENT_CACHE_TTL_MS = 24 * 60 * 60 * 1000;
const PUBLIC_MENU_PERSISTENT_CACHE_PREFIX = "mozzoapp:public-menu-cache:v7:";
const PUBLIC_MENU_IMAGE_REFERENCE_PATTERN =
  /^mozzoapp-public-image:(product|ingredient):([0-9a-f-]{36}):([a-f0-9]{16,64})$/i;
const publicMenuReadCache = new Map();
const publicMenuReadsInFlight = new Map();

const isTransientPublicMenuError = (error) =>
  ["57014", "REQUEST_TIMEOUT", "PGRST000", "PGRST001", "PGRST002", "PGRST003"].includes(error?.code) ||
  [502, 503, 504].includes(Number(error?.status)) ||
  /statement timeout|failed to fetch|networkerror|network request failed/i.test(error?.message || "");

const clearPublicMenuCache = (qrToken, cacheKey) => {
  publicMenuReadCache.delete(cacheKey);
  try {
    if (typeof window !== "undefined") {
      window.localStorage.removeItem(getPersistentPublicMenuCacheKey(qrToken));
    }
  } catch (error) {
    // Storage may be unavailable in private browsing.
  }
};

const normalizeQrAccessDurationMinutes = (value) =>
  Math.min(
    720,
    Math.max(15, Number(value || DEFAULT_QR_ACCESS_DURATION_MINUTES) || DEFAULT_QR_ACCESS_DURATION_MINUTES)
  );

const isMissingQrAccessDurationColumnError = (error) => {
  const message = `${error?.message || ""} ${error?.details || ""}`.toLowerCase();

  return error?.code === "42703" || message.includes("qr_access_duration_minutes");
};

const cloneJsonPayload = (value) => JSON.parse(JSON.stringify(value));

const getPersistentPublicMenuCacheKey = (qrToken = "") =>
  `${PUBLIC_MENU_PERSISTENT_CACHE_PREFIX}${String(qrToken || "").trim()}`;

const resolvePublicMenuImageReference = (value = "") => {
  const safeValue = String(value || "").trim();
  const match = safeValue.match(PUBLIC_MENU_IMAGE_REFERENCE_PATTERN);

  if (!match || !supabaseUrl) {
    return safeValue;
  }

  const [, kind, id, version] = match;
  const query = new URLSearchParams({ kind, id, v: version });

  return `${supabaseUrl}/functions/v1/public-menu-image?${query.toString()}`;
};

const normalizePublicMenuImageReferences = (payload) => {
  if (!payload || typeof payload !== "object") {
    return payload;
  }

  return {
    ...payload,
    products: (Array.isArray(payload.products) ? payload.products : []).map(
      (product) => ({
        ...product,
        imageUrl: resolvePublicMenuImageReference(
          product?.imageUrl || product?.image_url || ""
        ),
        ingredients: (Array.isArray(product?.ingredients)
          ? product.ingredients
          : []
        ).map((ingredient) => ({
          ...ingredient,
          imageUrl: resolvePublicMenuImageReference(
            ingredient?.imageUrl || ingredient?.image_url || ""
          ),
        })),
      })
    ),
  };
};

export const getCachedPublicMenuByQrToken = (
  qrToken,
  { maxAgeMs = PUBLIC_MENU_PERSISTENT_CACHE_TTL_MS } = {}
) => {
  const safeQrToken = String(qrToken || "").trim();

  if (!safeQrToken || typeof window === "undefined") {
    return null;
  }

  try {
    const cachedEntry = JSON.parse(
      window.localStorage.getItem(getPersistentPublicMenuCacheKey(safeQrToken)) ||
        "null"
    );
    const savedAt = Number(cachedEntry?.savedAt || 0);

    if (
      !cachedEntry?.payload ||
      !savedAt ||
      Date.now() - savedAt > Math.max(0, Number(maxAgeMs || 0))
    ) {
      return null;
    }

    return cloneJsonPayload(cachedEntry.payload);
  } catch (error) {
    return null;
  }
};

const savePersistentPublicMenuByQrToken = (qrToken, payload) => {
  const safeQrToken = String(qrToken || "").trim();

  if (!safeQrToken || !payload || typeof window === "undefined") {
    return;
  }

  try {
    window.localStorage.setItem(
      getPersistentPublicMenuCacheKey(safeQrToken),
      JSON.stringify({ savedAt: Date.now(), payload })
    );
  } catch (error) {
    // La carta sigue funcionando aunque el dispositivo no permita cache local.
  }
};

const pruneExpiredPublicMenuReadCache = () => {
  const now = Date.now();

  Array.from(publicMenuReadCache.entries()).forEach(([cacheKey, entry]) => {
    if (!entry || entry.expiresAt <= now) {
      publicMenuReadCache.delete(cacheKey);
    }
  });
};

const getCachedPublicMenuRead = (cacheKey) => {
  pruneExpiredPublicMenuReadCache();

  const entry = publicMenuReadCache.get(cacheKey);

  if (!entry || entry.expiresAt <= Date.now()) {
    if (entry) {
      publicMenuReadCache.delete(cacheKey);
    }

    return null;
  }

  return cloneJsonPayload(entry.value);
};

const saveCachedPublicMenuRead = (cacheKey, value) => {
  if (!cacheKey) {
    return cloneJsonPayload(value);
  }

  pruneExpiredPublicMenuReadCache();
  publicMenuReadCache.set(cacheKey, {
    value: cloneJsonPayload(value),
    expiresAt: Date.now() + PUBLIC_MENU_CACHE_TTL_MS,
  });

  if (publicMenuReadCache.size > 48) {
    Array.from(publicMenuReadCache.entries())
      .sort((left, right) => left[1].expiresAt - right[1].expiresAt)
      .slice(0, publicMenuReadCache.size - 32)
      .forEach(([staleKey]) => {
        publicMenuReadCache.delete(staleKey);
      });
  }

  return cloneJsonPayload(value);
};

const normalizeEntityId = (value) => String(value || "").trim();

const normalizeDisplayPairingCodeValue = (value) =>
  String(value || "")
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, "");

const formatDisplayPairingCode = (value) => {
  const normalizedValue = normalizeDisplayPairingCodeValue(value);

  if (!normalizedValue) {
    return "";
  }

  const normalizedBody = normalizedValue.startsWith(DISPLAY_PAIRING_PREFIX)
    ? normalizedValue.slice(DISPLAY_PAIRING_PREFIX.length)
    : normalizedValue;
  const codeGroups = normalizedBody.match(/.{1,4}/g) || [normalizedBody];

  return `${DISPLAY_PAIRING_PREFIX}-${codeGroups.join("-")}`;
};

const isMissingPublicKioskRpcError = (error) => {
  const message = `${error?.message || ""} ${error?.details || ""}`.toLowerCase();

  return (
    error?.code === "42883" ||
    error?.code === "PGRST202" ||
    message.includes("could not find the function") ||
    message.includes("get_public_kiosk_menu_by_branch")
  );
};

const isMissingPublicKioskOperationalFlagsError = (error) => {
  const message = `${error?.message || ""} ${error?.details || ""}`.toLowerCase();

  return (
    error?.code === "42703" ||
    message.includes("stock_enabled") ||
    message.includes("waiter_enabled") ||
    message.includes("ai_enabled")
  );
};

const isMissingPublicKioskModeColumnsError = (error) => {
  const message = `${error?.message || ""} ${error?.details || ""}`.toLowerCase();

  return (
    error?.code === "42703" ||
    message.includes("service_mode") ||
    message.includes("kiosk_menu_mode") ||
    message.includes("kiosk_station_count")
  );
};

const isMissingPublicKioskBrandAssetsError = (error) => {
  const message = `${error?.message || ""} ${error?.details || ""}`.toLowerCase();

  return (
    error?.code === "42703" ||
    message.includes("logo_compact_url") ||
    message.includes("icon_logo_url") ||
    message.includes("loading_logo_url") ||
    message.includes("kiosk_dine_in_image_url") ||
    message.includes("kiosk_takeaway_image_url") ||
    message.includes("loading_animation")
  );
};

const isMissingPublicDisplayRpcError = (error) => {
  const message = `${error?.message || ""} ${error?.details || ""}`.toLowerCase();

  return (
    error?.code === "42883" ||
    error?.code === "PGRST202" ||
    message.includes("could not find the function") ||
    message.includes("get_public_display_board_by_pairing_code")
  );
};

const isMissingPublicKitchenRpcError = (error) => {
  const message = `${error?.message || ""} ${error?.details || ""}`.toLowerCase();

  return (
    error?.code === "42883" ||
    error?.code === "PGRST202" ||
    message.includes("could not find the function") ||
    message.includes("get_public_kitchen_board_by_pairing_code")
  );
};

const isMissingPublicKitchenActionRpcError = (error) => {
  const message = `${error?.message || ""} ${error?.details || ""}`.toLowerCase();

  return (
    error?.code === "42883" ||
    error?.code === "PGRST202" ||
    message.includes("could not find the function") ||
    message.includes("apply_public_kitchen_board_action")
  );
};

const normalizePublicBranchPayload = (branch) => {
  if (!branch || typeof branch !== "object") {
    return branch;
  }

  const manualOperationsBlockingReason = String(
    branch?.manualOperationsBlockingReason || branch?.manual_operations_blocking_reason || ""
  )
    .trim()
    .slice(0, 240);
  const operationalBlockingReason = String(
    branch?.operationalBlockingReason || branch?.operational_blocking_reason || ""
  )
    .trim()
    .slice(0, 240);

  return {
    ...resolveBranchChannelCommissionRates(branch),
    ...branch,
    qrMenuStyle: normalizeQrMenuStyle(branch?.qrMenuStyle ?? branch?.qr_menu_style),
    qrCategoryOrder: normalizeQrCategoryOrder(branch?.qrCategoryOrder ?? branch?.qr_category_order),
    qrSubcategoryOrder: normalizeQrSubcategoryOrder(branch?.qrSubcategoryOrder ?? branch?.qr_subcategory_order),
    waiterAssistanceConfig: normalizeWaiterAssistanceConfig(
      branch?.waiterAssistanceConfig ?? branch?.waiter_assistance_config
    ),
    logoCompactUrl:
      branch?.logoCompactUrl ||
      branch?.logo_compact_url ||
      branch?.logoUrl ||
      branch?.logo_url ||
      "",
    iconLogoUrl:
      branch?.iconLogoUrl ||
      branch?.icon_logo_url ||
      branch?.logoCompactUrl ||
      branch?.logo_compact_url ||
      branch?.logoUrl ||
      branch?.logo_url ||
      "",
    loadingLogoUrl:
      branch?.loadingLogoUrl ||
      branch?.loading_logo_url ||
      branch?.iconLogoUrl ||
      branch?.icon_logo_url ||
      branch?.logoCompactUrl ||
      branch?.logo_compact_url ||
      branch?.logoUrl ||
      branch?.logo_url ||
      "",
    kioskDineInImageUrl:
      branch?.kioskDineInImageUrl || branch?.kiosk_dine_in_image_url || "",
    kioskTakeawayImageUrl:
      branch?.kioskTakeawayImageUrl || branch?.kiosk_takeaway_image_url || "",
    fontFamily: branch?.fontFamily || branch?.font_family || "",
    loadingAnimation:
      branch?.loadingAnimation === "pulse" || branch?.loadingAnimation === "float"
        ? branch.loadingAnimation
        : branch?.loading_animation === "pulse" || branch?.loading_animation === "float"
          ? branch.loading_animation
          : "spin",
    kitchenMode:
      branch?.kitchenMode === "screen" || branch?.kitchenMode === "printer"
        ? branch.kitchenMode
        : branch?.kitchen_mode === "screen" || branch?.kitchen_mode === "printer"
          ? branch.kitchen_mode
          : "manual",
    serviceMode:
      branch?.serviceMode === "kiosk"
        ? "kiosk"
        : branch?.service_mode === "kiosk"
          ? "kiosk"
          : "salon",
    stockEnabled:
      branch?.stockEnabled ?? branch?.stock_enabled ?? true,
    waiterEnabled:
      branch?.waiterEnabled ?? branch?.waiter_enabled ?? true,
    aiEnabled:
      branch?.aiEnabled ?? branch?.ai_enabled ?? true,
    kioskMenuMode:
      branch?.kioskMenuMode === "custom"
        ? "custom"
        : branch?.kiosk_menu_mode === "custom"
          ? "custom"
          : "current",
    kioskStationCount: Math.min(
      12,
      Math.max(
        1,
        Number(branch?.kioskStationCount ?? branch?.kiosk_station_count ?? 1) || 1
      )
    ),
    paymentProvider: normalizePaymentProvider(
      branch?.paymentProvider ||
        branch?.payment_provider ||
        DEFAULT_BRANCH_PAYMENT_PROVIDER
    ),
    paymentProviderStatus: normalizePaymentProviderStatus(
      branch?.paymentProviderStatus ||
        branch?.payment_provider_status ||
        DEFAULT_BRANCH_PAYMENT_PROVIDER_STATUS
    ),
    paymentAccountReference:
      branch?.paymentAccountReference || branch?.payment_account_reference || "",
    paymentAccountCbu:
      branch?.paymentAccountCbu || branch?.payment_account_cbu || "",
    paymentAccountAlias:
      branch?.paymentAccountAlias || branch?.payment_account_alias || "",
    commissionRate: normalizeCommissionRate(
      branch?.manualOrderCommissionRate ??
        branch?.manual_order_commission_rate ??
        branch?.commissionRate ??
        branch?.commission_rate ??
        DEFAULT_BRANCH_COMMISSION_RATE
    ),
    manualOrderCommissionRate: normalizeCommissionRate(
      branch?.manualOrderCommissionRate ??
        branch?.manual_order_commission_rate ??
        branch?.commissionRate ??
        branch?.commission_rate ??
        DEFAULT_BRANCH_COMMISSION_RATE
    ),
    menuQrCommissionRate: normalizeCommissionRate(
      branch?.menuQrCommissionRate ??
        branch?.menu_qr_commission_rate ??
        branch?.commissionRate ??
        branch?.commission_rate ??
        DEFAULT_BRANCH_COMMISSION_RATE
    ),
    kioskCommissionRate: normalizeCommissionRate(
      branch?.kioskCommissionRate ??
        branch?.kiosk_commission_rate ??
        branch?.commissionRate ??
        branch?.commission_rate ??
        DEFAULT_BRANCH_COMMISSION_RATE
    ),
    iaCommissionRate: normalizeCommissionRate(
      branch?.iaCommissionRate ??
        branch?.ia_commission_rate ??
        branch?.commissionRate ??
        branch?.commission_rate ??
        DEFAULT_BRANCH_COMMISSION_RATE
    ),
    paymentSplitMode: normalizePaymentSplitMode(
      branch?.paymentSplitMode ||
        branch?.payment_split_mode ||
        DEFAULT_BRANCH_PAYMENT_SPLIT_MODE
    ),
    kioskPaymentMethods: normalizePaymentMethods(
      branch?.kioskPaymentMethods ||
        branch?.kiosk_payment_methods ||
        DEFAULT_KIOSK_PAYMENT_METHODS,
      "kiosk"
    ),
    qrPaymentMethods: normalizePaymentMethods(
      branch?.qrPaymentMethods ||
        branch?.qr_payment_methods ||
        DEFAULT_QR_PAYMENT_METHODS,
      "qr"
    ),
    manualOperationsRuleEnabled:
      branch?.manualOperationsRuleEnabled ??
      branch?.manual_operations_rule_enabled ??
      true,
    manualOperationUnitsCost: Math.max(
      0,
      Math.round(
        Number(
          branch?.manualOperationUnitsCost ??
            branch?.manual_operation_units_cost ??
            0
        ) || 0
      )
    ),
    manualOperationsAvailable:
      branch?.manualOperationsAvailable ??
      branch?.manual_operations_available ??
      true,
    manualOperationsLowBalance:
      branch?.manualOperationsLowBalance ??
      branch?.manual_operations_low_balance ??
      false,
    manualOperationsBlockingReason,
    operationalAccessAllowed:
      branch?.operationalAccessAllowed ??
      branch?.operational_access_allowed ??
      true,
    operationalAccessMode:
      String(
        branch?.operationalAccessMode || branch?.operational_access_mode || ""
      )
        .trim()
        .toLowerCase() || "",
    operationalBlockingReason,
    menuQrWalletProviders: normalizeMenuQrWalletProviders(
      branch?.menuQrWalletProviders || branch?.menu_qr_wallet_providers
    ),
  };
};

const normalizePublicMenuPayload = (payload) => {
  if (!payload || typeof payload !== "object") {
    return payload;
  }

  const linkedRules = Array.isArray(payload.linkedRules) ? payload.linkedRules : [];
  const hiddenStandaloneSubcategoryIds = new Set();

  linkedRules.forEach((rule) => {
    const targetSubcategoryId = normalizeEntityId(
      rule?.targetSubcategoryId || rule?.target_subcategory_id
    );

    if (targetSubcategoryId) {
      hiddenStandaloneSubcategoryIds.add(targetSubcategoryId);
      return;
    }

    const targetCategoryId = normalizeEntityId(
      rule?.targetCategoryId || rule?.target_category_id
    );

    if (!targetCategoryId) {
      return;
    }

    (Array.isArray(payload.products) ? payload.products : []).forEach((product) => {
      const productCategoryId = normalizeEntityId(
        product?.categoryId || product?.category_id
      );
      const productSubcategoryId = normalizeEntityId(
        product?.subcategoryId || product?.subcategory_id
      );

      if (productCategoryId === targetCategoryId && productSubcategoryId) {
        hiddenStandaloneSubcategoryIds.add(productSubcategoryId);
      }
    });
  });

  const normalizedProducts = Array.isArray(payload.products)
    ? payload.products.map((product) => {
        const subcategoryId = normalizeEntityId(
          product?.subcategoryId || product?.subcategory_id
        );

        return {
          ...product,
          linkedOnly:
            product?.linkedOnly === true ||
            product?.linked_only === true ||
            hiddenStandaloneSubcategoryIds.has(subcategoryId),
        };
      })
    : [];
  const normalizedBranch = normalizePublicBranchPayload(payload?.branch);
  const normalizedIngredientStock =
    payload?.ingredientStock &&
    typeof payload.ingredientStock === "object" &&
    !Array.isArray(payload.ingredientStock)
      ? payload.ingredientStock
      : {};

  return {
    ...payload,
    branch: normalizedBranch,
    linkedRules,
    products: normalizedProducts,
    ingredientStock: normalizedIngredientStock,
  };
};

const normalizePublicQrPendingAccount = (account) => ({
  orderId: String(account?.orderId || account?.order_id || "").trim().slice(0, 160),
  customerName: String(
    account?.customerName || account?.customer_name || "Comensal"
  )
    .trim()
    .slice(0, 80),
  customerSessionId: String(
    account?.customerSessionId || account?.customer_session_id || ""
  )
    .trim()
    .slice(0, 160),
  orderedAt: String(account?.orderedAt || account?.ordered_at || "").trim(),
  phase: String(account?.phase || "new").trim().toLowerCase(),
  paymentStatus: String(
    account?.paymentStatus || account?.payment_status || "pending"
  )
    .trim()
    .toLowerCase(),
  paymentMethod: String(
    account?.paymentMethod || account?.payment_method || "counter"
  )
    .trim()
    .toLowerCase(),
  totalAmount: Math.max(0, Number(account?.totalAmount || account?.total_amount || 0)),
  digitalTotalAmount: account?.digitalTotalAmount == null ? null : Math.max(0, Number(account.digitalTotalAmount)),
  cashTotalAmount: account?.cashTotalAmount == null ? null : Math.max(0, Number(account.cashTotalAmount)),
  items: (Array.isArray(account?.items) ? account.items : []).map((item) => ({
    name: String(item?.name || item?.productName || item?.nombre || "Producto")
      .trim()
      .slice(0, 140),
    quantity: Math.max(1, Number(item?.quantity || 1)),
  })),
});

const normalizePublicQrPendingAccountsPayload = (payload) => ({
  branchId: String(payload?.branchId || payload?.branch_id || "").trim(),
  tableId: String(payload?.tableId || payload?.table_id || "").trim(),
  tableLabel: String(payload?.tableLabel || payload?.table_label || "").trim(),
  accounts: (Array.isArray(payload?.accounts) ? payload.accounts : [])
    .map(normalizePublicQrPendingAccount)
    .filter((account) => account.orderId && account.totalAmount > 0),
});

const normalizePublicDisplayOrder = (order) => ({
  id: String(order?.id || "").trim(),
  preparationNumber: Math.max(0, Number(order?.preparationNumber || 0)),
  customerName: String(order?.customerName || "").trim().slice(0, 80),
  displayName: String(order?.displayName || order?.customerName || order?.mesa || "Pedido")
    .trim()
    .slice(0, 80),
  orderedAt: String(order?.orderedAt || "").trim(),
  itemCount: Math.max(0, Number(order?.itemCount || 0)),
});

const normalizePublicDisplayBoardPayload = (payload) => {
  if (!payload || typeof payload !== "object") {
    return null;
  }

  return {
    branch: normalizePublicBranchPayload(payload.branch),
    pairingCode: formatDisplayPairingCode(payload.pairingCode || payload.pairing_code || ""),
    updatedAt: payload.updatedAt || payload.updated_at || "",
    preparingOrders: Array.isArray(payload.preparingOrders || payload.preparing_orders)
      ? (payload.preparingOrders || payload.preparing_orders).map((order) =>
          normalizePublicDisplayOrder(order)
        )
      : [],
    readyOrders: Array.isArray(payload.readyOrders || payload.ready_orders)
      ? (payload.readyOrders || payload.ready_orders).map((order) =>
          normalizePublicDisplayOrder(order)
        )
      : [],
  };
};

const normalizePublicKitchenOrder = (order) => {
  const kitchenItems = filterKitchenOrderItems(order?.items);

  return {
    id: String(order?.id || "").trim(),
    mesa: String(order?.mesa || "").trim(),
    tableId: String(order?.tableId || "").trim(),
    preparationNumber: Math.max(0, Number(order?.preparationNumber || 0)),
    customerName: String(order?.customerName || "").trim().slice(0, 80),
    displayName: String(order?.displayName || order?.customerName || order?.mesa || "Pedido")
      .trim()
      .slice(0, 80),
    orderedAt: String(order?.orderedAt || "").trim(),
    itemCount: kitchenItems.reduce(
      (total, item) => total + Math.max(1, Number(item?.quantity || 1)),
      0
    ),
    totalAmount: Math.max(0, Number(order?.totalAmount || 0)),
    canal: String(order?.canal || "").trim().slice(0, 60),
    comensales: Math.max(1, Number(order?.comensales || 1)),
    notes: String(order?.notes || "").trim().slice(0, 240),
    stage: String(order?.stage || "").trim(),
    items: kitchenItems.map((item) => ({
        name: String(item?.name || item?.nombre || item?.productName || "Producto")
          .trim()
          .slice(0, 120),
        quantity: Math.max(1, Number(item?.quantity || 1)),
        sendToKitchen: item?.sendToKitchen !== false,
      })),
  };
};

const normalizePublicKitchenBoardPayload = (payload) => {
  if (!payload || typeof payload !== "object") {
    return null;
  }

  return {
    branch: normalizePublicBranchPayload(payload.branch),
    pairingCode: formatDisplayPairingCode(payload.pairingCode || payload.pairing_code || ""),
    updatedAt: payload.updatedAt || payload.updated_at || "",
    newOrders: Array.isArray(payload.newOrders || payload.new_orders)
      ? (payload.newOrders || payload.new_orders)
          .map((order) => normalizePublicKitchenOrder(order))
          .filter(orderRequiresKitchen)
      : [],
    preparingOrders: Array.isArray(payload.preparingOrders || payload.preparing_orders)
      ? (payload.preparingOrders || payload.preparing_orders)
          .map((order) => normalizePublicKitchenOrder(order))
          .filter(orderRequiresKitchen)
      : [],
    readyOrders: Array.isArray(payload.readyOrders || payload.ready_orders)
      ? (payload.readyOrders || payload.ready_orders)
          .map((order) => normalizePublicKitchenOrder(order))
          .filter(orderRequiresKitchen)
      : [],
  };
};

const isPublicKitchenBoardPayload = (payload) =>
  Boolean(
    payload &&
      typeof payload === "object" &&
      (payload.branch ||
        payload.pairingCode ||
        payload.pairing_code ||
        Array.isArray(payload.newOrders || payload.new_orders) ||
        Array.isArray(payload.preparingOrders || payload.preparing_orders) ||
        Array.isArray(payload.readyOrders || payload.ready_orders))
  );

const createQrToken = (branchId, tableNumber) =>
  `qr_${(branchId || "branch").slice(0, 8)}_${tableNumber}_${Date.now()}_${Math.random()
    .toString(36)
    .slice(2, 8)}`;

export const getBranchScreenPairingCodes = async (branchIds = []) => {
  ensureSupabaseReady();

  const safeBranchIds = Array.from(
    new Set((Array.isArray(branchIds) ? branchIds : []).map(normalizeEntityId).filter(Boolean))
  );

  if (!safeBranchIds.length) {
    return {};
  }

  const { data, error } = await supabase.rpc(
    "get_or_create_branch_screen_pairing_codes",
    { p_branch_ids: safeBranchIds }
  );

  if (error) {
    throw normalizeError(
      error,
      "No pudimos cargar los codigos seguros de Cocina y Display."
    );
  }

  return Object.fromEntries(
    Object.entries(data && typeof data === "object" ? data : {}).map(
      ([branchId, pairingCode]) => [branchId, formatDisplayPairingCode(pairingCode)]
    )
  );
};

export const getBranchScreenPairingCode = async (branchId) => {
  const safeBranchId = normalizeEntityId(branchId);

  if (!safeBranchId) {
    throw new Error("No encontramos la sucursal para generar el codigo de pantalla.");
  }

  const pairingCodes = await getBranchScreenPairingCodes([safeBranchId]);
  const pairingCode = pairingCodes[safeBranchId] || "";

  if (!pairingCode) {
    throw new Error("No pudimos generar el codigo seguro de esta sucursal.");
  }

  return pairingCode;
};

export const rotateBranchScreenPairingCode = async (branchId) => {
  ensureSupabaseReady();

  const safeBranchId = normalizeEntityId(branchId);

  if (!safeBranchId) {
    throw new Error("No encontramos la sucursal para regenerar el codigo.");
  }

  const { data, error } = await supabase.rpc(
    "rotate_branch_screen_pairing_code",
    { p_branch_id: safeBranchId }
  );

  if (error) {
    throw normalizeError(error, "No pudimos regenerar el codigo de Cocina y Display.");
  }

  const pairingCode = formatDisplayPairingCode(data);

  if (!pairingCode) {
    throw new Error("El servidor no devolvio el nuevo codigo de pantalla.");
  }

  return pairingCode;
};

export const buildPublicQrUrl = (qrToken, origin = window.location.origin) => {
  const safeToken = String(qrToken || "").trim();
  const safeOrigin = String(origin || "").replace(/\/$/, "");

  return `${safeOrigin}/mesa/${encodeURIComponent(safeToken)}`;
};

export const buildPublicDisplayUrl = (
  pairingCode,
  origin = typeof window !== "undefined" ? window.location.origin : ""
) => {
  const safeOrigin = String(origin || "").replace(/\/$/, "");
  const safePairingCode = formatDisplayPairingCode(pairingCode);

  return `${safeOrigin}/display?code=${encodeURIComponent(safePairingCode)}`;
};

export const buildPublicKitchenUrl = (
  pairingCode,
  origin = typeof window !== "undefined" ? window.location.origin : ""
) => {
  const safeOrigin = String(origin || "").replace(/\/$/, "");
  const safePairingCode = formatDisplayPairingCode(pairingCode);

  return `${safeOrigin}/cocina?code=${encodeURIComponent(safePairingCode)}`;
};

const fetchBranchTables = async (branchId, { onlyActive = true } = {}) => {
  ensureSupabaseReady();

  if (!branchId) {
    throw new Error("No encontramos una sucursal activa para revisar sus mesas.");
  }

  let query = supabase
    .from("branch_tables")
    .select(
      "id, branch_id, table_number, label, active, created_at, table_qrs(id, qr_token, size_px, created_at, access_enabled_until, last_access_enabled_at)"
    )
    .eq("branch_id", branchId)
    .order("table_number", { ascending: true });

  if (onlyActive) {
    query = query.eq("active", true);
  }

  const { data, error } = await query;

  if (error) {
    throw normalizeError(error, "No se pudieron cargar las mesas con QR.");
  }

  return (data || []).map((row) => ({
    id: row.id,
    branchId: row.branch_id,
    tableNumber: row.table_number,
    label: row.label || "",
    active: row.active !== false,
    createdAt: row.created_at || null,
    qr: Array.isArray(row.table_qrs) && row.table_qrs.length > 0
        ? {
            id: row.table_qrs[0].id,
            token: row.table_qrs[0].qr_token,
            sizePx: Number(row.table_qrs[0].size_px || 128),
            createdAt: row.table_qrs[0].created_at || null,
            accessEnabledUntil: row.table_qrs[0].access_enabled_until || null,
            lastAccessEnabledAt: row.table_qrs[0].last_access_enabled_at || null,
          }
        : null,
  }));
};

export const listBranchTablesWithQrs = async (branchId) =>
  fetchBranchTables(branchId, { onlyActive: true });

export const createBranchTablesWithQrs = async (
  branchId,
  count,
  sizePx = 128,
  preferredTableNumbers = null
) => {
  ensureSupabaseReady();

  if (!branchId) {
    throw new Error("No encontramos la sucursal activa para crear sus mesas.");
  }

  const safeCount = Number(count || 0);
  if (!safeCount || safeCount < 1) {
    throw new Error("La cantidad de mesas debe ser mayor a cero.");
  }

  const allTables = await fetchBranchTables(branchId, { onlyActive: false });
  const tablesByNumber = new Map();

  allTables.forEach((table) => {
    const tableNumber = Number(table.tableNumber || 0);
    if (tableNumber > 0 && !tablesByNumber.has(tableNumber)) {
      tablesByNumber.set(tableNumber, table);
    }
  });

  const occupiedNumbers = new Set(
    allTables
      .filter((table) => table.active !== false && table.qr?.token)
      .map((table) => Number(table.tableNumber || 0))
      .filter((tableNumber) => tableNumber > 0)
  );
  const reusableTables = [];
  const nextTableNumbers = [];
  let candidateTableNumber = 1;

  while (reusableTables.length + nextTableNumbers.length < safeCount) {
    const existingTable = tablesByNumber.get(candidateTableNumber);

    if (existingTable) {
      if (!(existingTable.active !== false && existingTable.qr?.token)) {
        reusableTables.push(existingTable);
      }
    } else if (!occupiedNumbers.has(candidateTableNumber)) {
      nextTableNumbers.push(candidateTableNumber);
    }

    candidateTableNumber += 1;
  }

  const reusableTableIds = reusableTables.map((table) => table.id).filter(Boolean);

  if (reusableTableIds.length > 0) {
    const { error: reusableUpdateError } = await supabase
      .from("branch_tables")
      .update({ active: true })
      .in("id", reusableTableIds);

    if (reusableUpdateError) {
      throw normalizeError(reusableUpdateError, "No se pudieron reactivar las mesas disponibles.");
    }

    const reusableLabelUpdates = reusableTables
      .filter((table) => table.label !== `Mesa ${table.tableNumber}`)
      .map((table) =>
        supabase
          .from("branch_tables")
          .update({ label: `Mesa ${table.tableNumber}` })
          .eq("id", table.id)
      );

    if (reusableLabelUpdates.length > 0) {
      const reusableResults = await Promise.all(reusableLabelUpdates);
      const reusableLabelError = reusableResults.find((result) => result.error)?.error;

      if (reusableLabelError) {
        throw normalizeError(reusableLabelError, "No se pudieron normalizar las mesas reutilizadas.");
      }
    }

    const { error: previousQrsError } = await supabase
      .from("table_qrs")
      .delete()
      .in("table_id", reusableTableIds);

    if (previousQrsError) {
      throw normalizeError(previousQrsError, "No se pudieron limpiar los QR previos de las mesas reutilizadas.");
    }
  }

  let createdTables = [];

  if (nextTableNumbers.length > 0) {
    for (const targetTableNumber of nextTableNumbers) {
      const { data: insertedTable, error: tablesError } = await supabase
        .from("branch_tables")
        .insert({
          branch_id: branchId,
          table_number: targetTableNumber,
          label: `Mesa ${targetTableNumber}`,
          active: true,
        })
        .select("id, table_number")
        .single();

      if (tablesError) {
        throw normalizeError(tablesError, "No se pudieron crear las mesas.");
      }

      let normalizedInsertedTable = insertedTable;
      const returnedTableNumber = Number(insertedTable?.table_number || 0);

      if (returnedTableNumber !== targetTableNumber) {
        const { data: correctedTable, error: correctionError } = await supabase
          .from("branch_tables")
          .update({
            table_number: targetTableNumber,
            label: `Mesa ${targetTableNumber}`,
            active: true,
          })
          .eq("id", insertedTable.id)
          .select("id, table_number")
          .single();

        if (correctionError) {
          throw normalizeError(
            correctionError,
            `La base devolvio la mesa ${returnedTableNumber} cuando intentamos crear la mesa ${targetTableNumber}.`
          );
        }

        normalizedInsertedTable = correctedTable;
      }

      createdTables.push(normalizedInsertedTable);
    }
  }

  const nextQrsPayload = [
    ...reusableTables.map((table) => ({
      branch_id: branchId,
      table_id: table.id,
      qr_token: createQrToken(branchId, table.tableNumber),
      size_px: Number(sizePx || 128),
      access_enabled_until: new Date().toISOString(),
    })),
    ...createdTables.map((table) => ({
      branch_id: branchId,
      table_id: table.id,
      qr_token: createQrToken(branchId, table.table_number),
      size_px: Number(sizePx || 128),
      access_enabled_until: new Date().toISOString(),
    })),
  ];

  if (nextQrsPayload.length === 0) {
    return listBranchTablesWithQrs(branchId);
  }

  const { error: qrsError } = await supabase.from("table_qrs").insert(nextQrsPayload);

  if (qrsError) {
    throw normalizeError(qrsError, "No se pudieron crear los QR de las mesas.");
  }

  const finalTables = await listBranchTablesWithQrs(branchId);
  const expectedTableNumbers = [
    ...reusableTables
      .map((table) => Number(table.tableNumber || 0))
      .filter((tableNumber) => tableNumber > 0),
    ...nextTableNumbers,
  ];
  const missingExpectedTables = expectedTableNumbers.filter(
    (tableNumber) =>
      !finalTables.some(
        (table) => Number(table.tableNumber || 0) === tableNumber && table.qr?.token
      )
  );

  if (missingExpectedTables.length > 0) {
    const finalNumbers = finalTables
      .map((table) => Number(table.tableNumber || 0))
      .filter((tableNumber) => tableNumber > 0)
      .join(", ");
    throw new Error(
      `La base no devolvio las mesas esperadas (${missingExpectedTables.join(", ")}). Mesas activas devueltas: ${finalNumbers || "ninguna"}.`
    );
  }

  return finalTables;
};

export const deleteBranchTableWithQr = async (tableId) => {
  ensureSupabaseReady();

  if (!tableId) {
    throw new Error("No encontramos la mesa a eliminar.");
  }

  const { data, error } = await supabase
    .from("branch_tables")
    .delete()
    .eq("id", tableId)
    .select("id");

  if (error) {
    throw normalizeError(error, "No se pudo eliminar la mesa seleccionada.");
  }

  if (!Array.isArray(data) || data.length === 0) {
    throw new Error(
      "La mesa no se pudo eliminar en la base de datos. Recarga la pantalla e intentalo de nuevo."
    );
  }
};

export const getPublicMenuByQrToken = async (
  qrToken,
  { forceRefresh = false } = {}
) => {
  ensureSupabaseReady();

  if (!qrToken?.trim()) {
    throw new Error("No encontramos el token del QR.");
  }

  const safeQrToken = qrToken.trim();
  const access = await validateQrCustomerSession(safeQrToken);
  const cacheKey = `qr:${safeQrToken}`;
  const requestKey = `${cacheKey}:${access.customerSessionToken}`;
  const cachedPayload = getCachedPublicMenuRead(cacheKey);

  if (cachedPayload && !forceRefresh) {
    return cachedPayload;
  }

  const pendingRead = publicMenuReadsInFlight.get(requestKey);
  if (pendingRead) {
    return cloneJsonPayload(await pendingRead);
  }

  const read = (async () => {
    for (let attempt = 0; attempt < 2; attempt += 1) {
      try {
        const { data, error, status } = await withRequestDeadline((signal) =>
          supabase.rpc("get_public_menu_for_qr_session", {
            p_qr_token: safeQrToken,
            p_customer_session_token: access.customerSessionToken,
          }).abortSignal(signal)
        );

        if (error) {
          throw Object.assign(new Error(error.message), error, { status });
        }

        if (!data) {
          throw new Error("No encontramos informacion disponible para este QR.");
        }

        const normalizedPayload = normalizePublicMenuPayload(
          normalizePublicMenuImageReferences(data)
        );
        savePersistentPublicMenuByQrToken(safeQrToken, normalizedPayload);
        return saveCachedPublicMenuRead(cacheKey, normalizedPayload);
      } catch (error) {
        const transient = isTransientPublicMenuError(error);
        if (transient && attempt === 0) {
          await new Promise((resolve) => setTimeout(resolve, 400));
          continue;
        }
        if (!transient) {
          // A closed/invalid QR must never fall back to a previously valid menu.
          clearPublicMenuCache(safeQrToken, cacheKey);
        }
        throw Object.assign(new Error(transient
          ? "La carta esta tardando mas de lo esperado. Espera unos segundos y toca Reintentar."
          : error.message || "No se pudo abrir el menu publico de la mesa."), { code: error.code });
      }
    }
  })();

  publicMenuReadsInFlight.set(requestKey, read);
  try {
    return cloneJsonPayload(await read);
  } finally {
    publicMenuReadsInFlight.delete(requestKey);
  }
};

export const getPublicQrPendingAccounts = async (qrToken) => {
  ensureSupabaseReady();

  const safeQrToken = String(qrToken || "").trim();

  if (!safeQrToken) {
    throw new Error("No encontramos el QR de la mesa para revisar las cuentas.");
  }

  const access = await validateQrCustomerSession(safeQrToken);
  const { data, error } = await supabase.rpc("get_public_qr_session_pending_accounts", {
    p_qr_token: safeQrToken,
    p_customer_session_token: access.customerSessionToken,
  });

  if (error) {
    const errorMessage = `${error?.message || ""} ${error?.details || ""}`.toLowerCase();

    if (
      error?.code === "42883" ||
      error?.code === "PGRST202" ||
      errorMessage.includes("get_public_qr_pending_accounts")
    ) {
      throw new Error(
        "Falta aplicar qr_table_pending_payments_patch.sql para mostrar las cuentas pendientes de la mesa."
      );
    }

    throw normalizeError(error, "No se pudieron consultar las cuentas pendientes de la mesa.");
  }

  return normalizePublicQrPendingAccountsPayload(data || {});
};

export const finalizeApprovedPublicMenuPayment = async ({
  qrToken,
  paymentIntentId,
} = {}) => {
  ensureSupabaseReady();

  const safeQrToken = String(qrToken || "").trim();
  const safePaymentIntentId = String(paymentIntentId || "").trim();

  if (!safeQrToken || !safePaymentIntentId) {
    throw new Error("Faltan datos del pago aprobado para actualizar la mesa.");
  }

  const { data, error } = await supabase.rpc("finalize_approved_menu_payment", {
    p_payment_intent_id: safePaymentIntentId,
    p_qr_token: safeQrToken,
  });

  if (error) {
    const errorMessage = `${error?.message || ""} ${error?.details || ""}`.toLowerCase();

    if (
      error?.code === "42883" ||
      error?.code === "PGRST202" ||
      errorMessage.includes("finalize_approved_menu_payment")
    ) {
      return {
        supported: false,
        finalized: false,
        reason:
          "Falta aplicar qr_table_pending_payments_patch.sql para finalizar el pago en la mesa.",
      };
    }

    throw normalizeError(error, "No se pudo aplicar el pago aprobado a la mesa.");
  }

  return {
    supported: true,
    finalized: data?.finalized === true,
    paymentContext: String(data?.paymentContext || data?.payment_context || "").trim(),
    order: data?.order || null,
    orders: Array.isArray(data?.orders) ? data.orders : [],
    orderIds: Array.isArray(data?.orderIds || data?.order_ids)
      ? data?.orderIds || data?.order_ids
      : [],
    customerSessionToken: String(
      data?.customerSessionToken || data?.customer_session_token || ""
    ).trim(),
    paidAt: String(data?.paidAt || data?.paid_at || "").trim(),
  };
};

export const getPublicBranchPaymentSetup = async (branchId) => {
  ensureSupabaseReady();

  if (!branchId?.trim()) {
    throw new Error("No encontramos la sucursal para revisar sus pagos publicos.");
  }

  const { data, error } = await supabase.functions.invoke(
    "public-branch-payment-setup",
    {
      body: {
        branchId: branchId.trim(),
      },
    }
  );

  if (error) {
    throw normalizeError(
      error,
      "No se pudo revisar la configuracion publica de pagos de la sucursal."
    );
  }

  return normalizePublicBranchPayload(data);
};

export const getBranchQrAccessDurationMinutes = async (branchId) => {
  ensureSupabaseReady();

  if (!branchId) {
    throw new Error("No encontramos la sucursal activa para leer la duracion del QR.");
  }

  const { data, error } = await supabase
    .from("branch_settings")
    .select("qr_access_duration_minutes")
    .eq("branch_id", branchId)
    .maybeSingle();

  if (error) {
    if (isMissingQrAccessDurationColumnError(error)) {
      throw new Error(
        "Falta aplicar qr_access_duration_settings_patch.sql en Supabase antes de configurar cuantas horas dura el acceso QR."
      );
    }

    throw normalizeError(error, "No se pudo leer la duracion configurada del QR.");
  }

  return normalizeQrAccessDurationMinutes(data?.qr_access_duration_minutes);
};

export const updateBranchQrAccessDurationMinutes = async (
  branchId,
  durationMinutes = DEFAULT_QR_ACCESS_DURATION_MINUTES
) => {
  ensureSupabaseReady();

  if (!branchId) {
    throw new Error("No encontramos la sucursal activa para guardar la duracion del QR.");
  }

  const safeDurationMinutes = normalizeQrAccessDurationMinutes(durationMinutes);

  const { error } = await supabase.from("branch_settings").upsert({
    branch_id: branchId,
    qr_access_duration_minutes: safeDurationMinutes,
  });

  if (error) {
    if (isMissingQrAccessDurationColumnError(error)) {
      throw new Error(
        "Falta aplicar qr_access_duration_settings_patch.sql en Supabase antes de guardar cuantas horas dura el acceso QR."
      );
    }

    throw normalizeError(error, "No se pudo guardar la duracion del acceso QR.");
  }

  return safeDurationMinutes;
};

export const enableBranchTableQrAccess = async (
  tableId,
  durationMinutes = DEFAULT_QR_ACCESS_DURATION_MINUTES
) => {
  ensureSupabaseReady();

  if (!tableId) {
    throw new Error("No encontramos la mesa para habilitar su QR.");
  }

  const safeDurationMinutes = normalizeQrAccessDurationMinutes(durationMinutes);
  const now = Date.now();
  const enabledUntil = new Date(now + safeDurationMinutes * 60 * 1000).toISOString();
  const lastEnabledAt = new Date(now).toISOString();

  const { data, error } = await supabase
    .from("table_qrs")
    .update({
      access_enabled_until: enabledUntil,
      last_access_enabled_at: lastEnabledAt,
    })
    .eq("table_id", tableId)
    .select("id, table_id, access_enabled_until, last_access_enabled_at")
    .single();

  if (error) {
    throw normalizeError(error, "No se pudo habilitar el QR de la mesa.");
  }

  return {
    id: data?.id || "",
    tableId: data?.table_id || tableId,
    accessEnabledUntil: data?.access_enabled_until || enabledUntil,
    lastAccessEnabledAt: data?.last_access_enabled_at || lastEnabledAt,
  };
};

export const enableBranchTablesQrAccess = async (
  branchId,
  durationMinutes = DEFAULT_QR_ACCESS_DURATION_MINUTES
) => {
  ensureSupabaseReady();

  const safeBranchId = normalizeEntityId(branchId);

  if (!safeBranchId) {
    throw new Error("No encontramos la sucursal para habilitar sus mesas.");
  }

  const safeDurationMinutes = normalizeQrAccessDurationMinutes(durationMinutes);
  const now = Date.now();
  const enabledUntil = new Date(now + safeDurationMinutes * 60 * 1000).toISOString();
  const lastEnabledAt = new Date(now).toISOString();

  const { data, error } = await supabase
    .from("table_qrs")
    .update({
      access_enabled_until: enabledUntil,
      last_access_enabled_at: lastEnabledAt,
    })
    .eq("branch_id", safeBranchId)
    .select("id, table_id, access_enabled_until, last_access_enabled_at");

  if (error) {
    throw normalizeError(error, "No se pudieron habilitar los QR de las mesas.");
  }

  const updatedRows = Array.isArray(data) ? data : [];

  if (updatedRows.length === 0) {
    throw new Error("No encontramos mesas con QR para habilitar en esta sucursal.");
  }

  return updatedRows.map((row) => ({
    id: row?.id || "",
    tableId: row?.table_id || "",
    accessEnabledUntil: row?.access_enabled_until || enabledUntil,
    lastAccessEnabledAt: row?.last_access_enabled_at || lastEnabledAt,
  }));
};

export const disableBranchTableQrAccess = async (tableId) => {
  ensureSupabaseReady();

  if (!tableId) {
    throw new Error("No encontramos la mesa para cerrar su QR.");
  }

  const disabledAt = new Date().toISOString();

  const { data, error } = await supabase
    .from("table_qrs")
    .update({
      access_enabled_until: disabledAt,
      access_disabled_at: disabledAt,
    })
    .eq("table_id", tableId)
    .select("id, table_id, access_enabled_until, last_access_enabled_at")
    .single();

  if (error) {
    throw normalizeError(error, "No se pudo cerrar el QR de la mesa.");
  }

  const { error: revokeSessionsError } = await supabase
    .from("customer_sessions")
    .update({
      status: "revoked",
      last_seen_at: disabledAt,
      closed_at: disabledAt,
      expires_at: disabledAt,
    })
    .eq("table_id", tableId)
    .eq("status", "active");

  if (revokeSessionsError) {
    throw normalizeError(
      revokeSessionsError,
      "Cerramos el QR, pero no pudimos revocar las sesiones activas de la mesa."
    );
  }

  return {
    id: data?.id || "",
    tableId: data?.table_id || tableId,
    accessEnabledUntil: data?.access_enabled_until || disabledAt,
    lastAccessEnabledAt: data?.last_access_enabled_at || null,
  };
};

export const getPublicKioskMenuByBranchId = async (branchId, stationNumber = 1) => {
  ensureSupabaseReady();

  if (!branchId?.trim()) {
    throw new Error("No encontramos la sucursal del totem.");
  }

  const safeBranchId = branchId.trim();
  const safeStationNumber = Math.min(
    12,
    Math.max(1, Number(stationNumber || 1) || 1)
  );
  const cacheKey = `kiosk:${safeBranchId}:${safeStationNumber}`;
  const cachedPayload = getCachedPublicMenuRead(cacheKey);

  if (cachedPayload) {
    return cachedPayload;
  }

  const { data, error } = await supabase.rpc("get_public_kiosk_menu_by_branch", {
    p_branch_id: safeBranchId,
    p_station_number: safeStationNumber,
  });

  if (error) {
    if (isMissingPublicKioskRpcError(error)) {
      throw new Error(
        "Falta aplicar public_kiosk_menu_patch.sql en Supabase para abrir el totem desde otro navegador o dispositivo."
      );
    }

    if (isMissingPublicKioskOperationalFlagsError(error)) {
      throw new Error(
        "Falta aplicar branch_operational_feature_flags_patch.sql en Supabase para que el totem publico pueda leer stock, mozo e IA de la sucursal."
      );
    }

    if (isMissingPublicKioskModeColumnsError(error)) {
      throw new Error(
        "Falta aplicar kiosk_mode_patch.sql en Supabase para que el totem publico pueda leer la modalidad kiosk de la sucursal."
      );
    }

    if (isMissingPublicKioskBrandAssetsError(error)) {
      throw new Error(
        "Falta aplicar branch_brand_assets_patch.sql en Supabase para que el totem publico pueda leer mini logos, logo de carga e imagenes del ingreso kiosk."
      );
    }

    throw normalizeError(error, "No se pudo abrir la carta publica del totem.");
  }

  if (!data) {
    throw new Error("No encontramos informacion publica para este totem.");
  }

  return saveCachedPublicMenuRead(
    cacheKey,
    normalizePublicMenuPayload(data)
  );
};

export const getPublicDisplayBoardByPairingCode = async (pairingCode) => {
  ensureSupabaseReady();

  const safePairingCode = formatDisplayPairingCode(pairingCode);

  if (!safePairingCode) {
    throw new Error("Necesitamos un codigo de vinculacion para abrir esta pantalla.");
  }

  const { data, error } = await supabase.rpc(
    "get_public_display_board_by_pairing_code",
    {
      p_pairing_code: safePairingCode,
    }
  );

  if (error) {
    if (isMissingPublicDisplayRpcError(error)) {
      throw new Error(
        "Falta aplicar public_display_board_patch.sql en Supabase para abrir la pantalla publica de preparacion y retiro."
      );
    }

    throw normalizeError(error, "No se pudo abrir la pantalla publica de pedidos.");
  }

  if (!data) {
    throw new Error("No encontramos una sucursal activa para ese codigo de pantalla.");
  }

  return normalizePublicDisplayBoardPayload(data);
};

export const getPublicKitchenBoardByPairingCode = async (pairingCode) => {
  ensureSupabaseReady();

  const safePairingCode = formatDisplayPairingCode(pairingCode);

  if (!safePairingCode) {
    throw new Error("Necesitamos un codigo de vinculacion para abrir esta cocina.");
  }

  const { data, error } = await supabase.rpc(
    "get_public_kitchen_board_by_pairing_code",
    {
      p_pairing_code: safePairingCode,
    }
  );

  if (error) {
    if (isMissingPublicKitchenRpcError(error)) {
      throw new Error(
        "Falta aplicar public_kitchen_board_patch.sql en Supabase para abrir la cocina publica por codigo."
      );
    }

    throw normalizeError(error, "No se pudo abrir la pantalla publica de cocina.");
  }

  if (!data) {
    throw new Error("No encontramos una sucursal activa para ese codigo de cocina.");
  }

  return normalizePublicKitchenBoardPayload(data);
};

export const applyPublicKitchenBoardAction = async ({
  pairingCode,
  orderId,
  action,
}) => {
  ensureSupabaseReady();

  const safePairingCode = formatDisplayPairingCode(pairingCode);
  const safeOrderId = String(orderId || "").trim();
  const safeAction = String(action || "").trim().toLowerCase();

  if (!safePairingCode) {
    throw new Error("Necesitamos el codigo de vinculacion para operar esta cocina.");
  }

  if (!safeOrderId) {
    throw new Error("No encontramos el pedido que quieres mover.");
  }

  if (!["take", "ready", "return"].includes(safeAction)) {
    throw new Error("La accion de cocina no es valida.");
  }

  const { data, error } = await supabase.rpc("apply_public_kitchen_board_action", {
    p_pairing_code: safePairingCode,
    p_order_id: safeOrderId,
    p_action: safeAction,
  });

  if (error) {
    if (isMissingPublicKitchenActionRpcError(error)) {
      throw new Error(
        "Falta aplicar public_kitchen_board_patch.sql en Supabase para operar la cocina publica por codigo."
      );
    }

    throw normalizeError(error, "No se pudo mover el pedido en la cocina publica.");
  }

  if (isPublicKitchenBoardPayload(data)) {
    return normalizePublicKitchenBoardPayload(data);
  }

  return {
    success: data?.success !== false,
    branchId: String(data?.branchId || data?.branch_id || "").trim(),
    orderId: String(data?.orderId || data?.order_id || safeOrderId).trim(),
    action: String(data?.action || safeAction).trim().toLowerCase(),
    updatedAt: String(data?.updatedAt || data?.updated_at || "").trim(),
  };
};
